import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import { Radar, LayoutGrid, GitCompareArrows, Bell, Sparkles, BarChart3, ServerCog, Calculator, Gauge, Blocks, ChevronDown, Plus, Settings, Building2, Check, Zap, Route } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FeedbackButton } from "@/components/FeedbackButton";
import ProfileMenu from "@/components/ProfileMenu";
import InstallAppButton from "@/components/InstallAppButton";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { plan, isAdmin } = useAuth();
  const [unread, setUnread] = useState(0);

  const loadUnread = useCallback(async () => {
    try {
      const res = await api.get("/changes");
      setUnread(res.data.unread || 0);
    } catch {}
  }, []);

  useEffect(() => {
    loadUnread();
    const onUpdate = () => loadUnread();
    window.addEventListener("changes-updated", onUpdate);
    return () => window.removeEventListener("changes-updated", onUpdate);
  }, [loadUnread, location.pathname]);

  const nav = [
    { path: "/command", label: "Command", icon: Gauge },
    { path: "/journey", label: "Journey", icon: Route },
    { path: "/dashboard", label: "Scanner", icon: Radar },
    { path: "/integrations", label: "Integrations", icon: Blocks },
    { path: "/automations", label: "Automations", icon: Zap },
    { path: "/compare", label: "Compare", icon: GitCompareArrows },
    { path: "/changes", label: "Alerts", icon: Bell, badge: unread },
    { path: "/migration", label: "Savings", icon: Calculator },
    ...((plan === "agency" || isAdmin) ? [{ path: "/backend-scan", label: "Backend Scan", icon: ServerCog }] : []),
    ...(isAdmin ? [{ path: "/admin", label: "Analytics", icon: BarChart3 }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header
        className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-[72px] flex items-center justify-between gap-3">
          <button data-testid="logo-home" onClick={() => navigate("/command")} className="flex items-center shrink-0">
            <img src="/brand-logo-new.png" alt="Link Website Pro" className="h-11 sm:h-14 w-auto object-contain bg-white rounded-xl px-2.5 py-1.5 shadow-sm" />
          </button>

          <div className="hidden sm:block h-6 w-px bg-white/10 shrink-0" />

          <nav className="hidden lg:flex items-center gap-1 overflow-x-auto no-scrollbar mx-1 flex-1 min-w-0 justify-start">
            {nav.map((n) => {
              const active = location.pathname === n.path;
              return (
                <button
                  key={n.path}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  onClick={() => navigate(n.path)}
                  className={`relative flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors shrink-0 ${
                    active ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <n.icon size={16} />
                  <span className="hidden xl:block">{n.label}</span>
                  {n.badge > 0 && (
                    <span
                      data-testid="alerts-badge"
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center"
                    >
                      {n.badge > 9 ? "9+" : n.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <InstallAppButton variant="ghost" />
            {plan === "free" && (
              <button
                data-testid="upgrade-nav-btn"
                onClick={() => navigate("/pricing")}
                className="flex items-center gap-1.5 btn-brand text-white text-sm font-medium rounded-full px-3.5 py-1.5 transition-colors"
              >
                <Sparkles size={14} /> <span className="hidden sm:block">Upgrade</span>
              </button>
            )}
            <ProfileMenu />
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 lg:pb-8">{children}</main>
      <footer className="border-t border-white/10 mt-8" data-testid="app-footer">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-zinc-600 text-xs">© 2026 Link‑Lite Data LLC · Link Website Pro</span>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-zinc-500">
            <a href="/legal/terms.html" className="hover:text-white transition-colors">Terms</a>
            <a href="/legal/privacy.html" className="hover:text-white transition-colors">Privacy</a>
            <a href="/legal/acceptable-use.html" className="hover:text-white transition-colors">Acceptable Use</a>
            <a href="/legal/cookies.html" className="hover:text-white transition-colors">Cookies</a>
            <a href="/legal/refund.html" className="hover:text-white transition-colors">Refunds</a>
            <a href="/legal/security.html" className="hover:text-white transition-colors">Security</a>
            <a href="/legal/sla.html" className="hover:text-white transition-colors">SLA</a>
            <a href="/legal/api-docs.html" className="hover:text-white transition-colors">API</a>
            <a href="/legal/" className="hover:text-white transition-colors font-medium text-zinc-400">Legal Center</a>
          </nav>
        </div>
      </footer>
      <FeedbackButton />

      {/* Mobile bottom navigation — persistent "way back" to every section */}
      <nav
        data-testid="mobile-bottom-nav"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-stretch overflow-x-auto no-scrollbar">
          {nav.map((n) => {
            const active = location.pathname === n.path;
            return (
              <button
                key={n.path}
                data-testid={`mobilenav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => navigate(n.path)}
                className={`relative flex flex-col items-center justify-center gap-1 py-2.5 px-3 min-w-[64px] flex-1 transition-colors ${
                  active ? "text-[color:var(--ws-accent,#4da3ff)]" : "text-zinc-500"
                }`}
              >
                {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-[color:var(--ws-accent,#4da3ff)]" />}
                <n.icon size={20} />
                <span className="text-[10px] font-medium whitespace-nowrap">{n.label}</span>
                {n.badge > 0 && (
                  <span className="absolute top-1.5 right-3 min-w-[16px] h-[16px] px-1 rounded-full bg-[#FF3B30] text-white text-[9px] font-bold flex items-center justify-center">
                    {n.badge > 9 ? "9+" : n.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
